import type { GameScene } from './GameScene.js';
import { appendFancySelect } from '../ui/fancySelect.js';
import { RaceHud } from '../play/raceHud.js';
import type { Car, Driver } from '@multirx/shared';
import { canPlayOtherDriver } from '@multirx/shared/types/user.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { getCurrentUser } from '../auth/session.js';
import { fetchCars } from '../cars/api.js';
import { fetchDrivers } from '../drivers/api.js';
import { fetchCarsByDivision, fetchDriversByDivision } from '../divisions/api.js';
import { saveRaceHistory } from '../raceHistory/api.js';
import { flushClientRaceDebugLogNow } from './raceDebugSetup.js';
import type { RaceHistoryPlayMode } from '@multirx/shared/types/raceHistory.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import { fetchCircuitById, fetchCircuits, type SavedCircuit } from '../circuits/api.js';
import { circuitSelectOptionLabel } from '../circuits/circuitDisplay.js';
import { createDiceCubeScene, setDiceCubeFace } from './diceCube.js';
import {
  resolveTrackCellBonusVisual,
  trackCellBonusVisualClass,
  TRACK_CELL_COLOR_CLASS_NAMES,
} from '@multirx/shared/game/trackCellBonusVisual.js';
import { fetchMeetingPlayConfig } from '../championship/api.js';
import { emitMeetingReclaimRace, onMeetingRaceComplete } from '../championship/meetingSocket.js';
import { showChampionshipQualCompletionRecaps } from '../championship/showChampionshipQualCompletionRecaps.js';
import { parseCourseHashParams } from '../championship/seasonPrefs.js';
import type { PlayConfig } from '@multirx/shared/types/championship.js';
import { shouldAttemptClientRaceHistorySave } from './raceEndClientSave.js';
import {
  mountCircuit3dPreview,
  type Circuit3dPreviewHandle,
  type CameraFollowMode,
} from '../circuits/circuit3dPreview.js';
import { CAMERA_MODE_OPTIONS, cameraModeLabel } from '../play/cameraModeLabels.js';
import {
  isAutoRollEnabled,
  syncAutoRollCheckbox,
  writeAutoRollPreference,
} from './autoRollPreference.js';

const CHECKMARK_SVG = `<svg stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5" stroke-linejoin="round" stroke-linecap="round" /></svg>`;

/** Petite pause avant le lancer auto — laisse le temps de voir "à vous de jouer" avant que le dé ne parte tout seul. */
const AUTO_ROLL_DELAY_MS = 600;

type PlayMode = 'solo' | 'multi-host' | 'multi-join';

type SetupStepId = 'mode' | 'circuit' | 'division' | 'roster' | 'player';

interface SetupStepDef {
  id: SetupStepId;
  label: string;
  title: string;
  lead: string;
}

const SETUP_STEP_DEFS: Record<SetupStepId, SetupStepDef> = {
  mode: {
    id: 'mode',
    label: 'Mode',
    title: 'Comment voulez-vous jouer ?',
    lead: 'Choisissez une option ci-dessous pour commencer.',
  },
  circuit: {
    id: 'circuit',
    label: 'Circuit',
    title: 'Circuit et nombre de tours',
    lead: 'Choisissez le tracé affiché sur le plateau, puis le nombre de tours à parcourir.',
  },
  division: {
    id: 'division',
    label: 'Division',
    title: 'Division championnat',
    lead: 'La division détermine quelles voitures et pilotes sont disponibles pour la course.',
  },
  roster: {
    id: 'roster',
    label: 'Effectif',
    title: 'La grille de départ',
    lead: 'Combien de voitures sur le circuit, et combien seront pilotées par de vrais joueurs ?',
  },
  player: {
    id: 'player',
    label: 'Votre duo',
    title: 'Voiture et pilote',
    lead: 'Choisissez la combinaison avec laquelle vous prenez le départ.',
  },
};

const STEPS_BY_MODE: Record<PlayMode, SetupStepId[]> = {
  solo: ['mode', 'circuit', 'division', 'roster', 'player'],
  'multi-host': ['mode', 'circuit', 'division', 'roster', 'player'],
  'multi-join': ['mode', 'player'],
};

/**
 * UI du dé et du tour de jeu — préparation par étapes ou en course (dé, classement)
 */
export class DiceUI {
  private wrapper: HTMLDivElement | null = null;
  private panel: HTMLDivElement | null = null;
  private blockPreRace: HTMLDivElement | null = null;
  private raceHud: RaceHud | null = null;
  private selectCircuit: HTMLSelectElement | null = null;
  private savedCircuits: SavedCircuit[] = [];
  private selectCar: HTMLSelectElement | null = null;
  private selectDriver: HTMLSelectElement | null = null;
  private selectParticipants: HTMLSelectElement | null = null;
  private selectHumans: HTMLSelectElement | null = null;
  private selectDivision: HTMLSelectElement | null = null;
  private selectLaps: HTMLSelectElement | null = null;
  private rosterParticipantsLabel: HTMLLabelElement | null = null;
  private rosterHumansLabel: HTMLLabelElement | null = null;
  private playMode: PlayMode = 'solo';
  private currentSetupStep = 0;
  private setupProgressBar: HTMLDivElement | null = null;
  private setupProgressChecks: HTMLElement[] = [];
  private setupProgressLabel: HTMLParagraphElement | null = null;
  private stepPanelsById: Partial<Record<SetupStepId, HTMLDivElement>> = {};
  private modeChoiceEls: HTMLButtonElement[] = [];
  private setupStepperHost: HTMLElement | null = null;
  private btnSetupBack: HTMLButtonElement | null = null;
  private btnSetupPrimary: HTMLButtonElement | null = null;
  private buttonRoll: HTMLButtonElement | null = null;
  private checkboxAutoRoll: HTMLInputElement | null = null;
  /** Vrai dès qu'un lancer auto a été programmé pour le tour courant — évite de
   * reprogrammer un `setTimeout` à chaque `refresh()` tant que le serveur n'a
   * pas confirmé le changement de tour (surtout en multijoueur, où `canRollDice()`
   * reste vrai le temps de l'aller-retour réseau après l'émission du lancer :
   * sans ce garde-fou, un lancer serait émis en boucle). Remis à faux dès que
   * `canRollDice()` redevient faux (tour transmis/terminé, voir `refresh`). */
  private autoRollPending = false;
  private labelTurn: HTMLSpanElement | null = null;
  private labelResult: HTMLSpanElement | null = null;
  private labelBonus: HTMLSpanElement | null = null;
  private diceCube: HTMLDivElement | null = null;
  private hintNoDriver: HTMLSpanElement | null = null;
  private labelRaceFinished: HTMLDivElement | null = null;
  private inRacePlayingBlock: HTMLDivElement | null = null;
  private gameScene: GameScene | null = null;
  private cars: Car[] = [];
  private drivers: Driver[] = [];
  private championshipMode = false;
  private historySaveInFlight = false;
  private meetingPlayConfig: PlayConfig | null = null;
  private meetingBootstrapDone = false;
  private meetingRaceCompleteTeardown: (() => void) | null = null;
  private testRaceBootstrapDone = false;
  private raceResultsDismissed = false;
  private view3dContainer: HTMLElement | null = null;
  private circuit3dPreview: Circuit3dPreviewHandle | null = null;
  private viewMode: '2d' | '3d' = '2d';
  /** Mode de suivi caméra 3D choisi par l'utilisateur — conservé indépendamment
   * du cycle de vie de `circuit3dPreview` (remonté à chaque changement de
   * circuit/passage en course live, voir `setViewMode`) pour ne pas revenir en
   * "Libre" à chaque remontage. */
  private cameraMode: CameraFollowMode = 'free';
  private cameraPickerRoot: HTMLDivElement | null = null;
  private cameraPickerBtn: HTMLButtonElement | null = null;
  private cameraPickerLabel: HTMLSpanElement | null = null;
  private cameraPickerMenu: HTMLUListElement | null = null;
  private cameraPickerDocClose: ((e: MouseEvent) => void) | null = null;
  /** Vrai si le contenu 3D actuellement monté est la course LIVE (voitures et
   * positions réelles des joueurs), faux s'il s'agit de l'aperçu statique
   * pré-course (voitures de démo génériques). Sert à détecter la transition
   * "course vient de démarrer alors qu'on était déjà en 3D" (voir
   * `setViewMode`) : sans ça, `viewMode` reste `'3d'` sans changer au moment
   * où la course démarre, donc rien ne remonte la scène avec les vraies
   * voitures — le plateau reste bloqué sur les voitures de démo génériques. */
  private live3DIsLiveRace = false;
  /** Vrai une fois qu'on a basculé automatiquement en 3D pour le circuit
   * actuellement chargé (voir `refresh`) — la vue 3D est le mode par défaut
   * dès qu'un circuit est chargé (aperçu pré-course ou course en cours), mais
   * ne doit re-basculer qu'une fois : sinon, un retour manuel de
   * l'utilisateur en 2D serait annulé au refresh suivant. Remis à zéro
   * uniquement quand plus aucun circuit n'est chargé. */
  private hasAutoSwitchedToLive3D = false;

  constructor(container: HTMLElement, gameScene: GameScene) {
    this.gameScene = gameScene;
    this.createUI(container);
    // La manche meeting est désormais server-autoritaire : c'est le serveur
    // qui persiste résultats/replay dès la fin de course (voir `finalizeMeetingRaceIfFinished`
    // côté serveur) — ce client se contente d'afficher le récapitulatif poussé.
    this.meetingRaceCompleteTeardown = onMeetingRaceComplete((response) => {
      if (!this.championshipMode) return;
      this.gameScene?.setHistorySaved(true);
      this.gameScene?.setResultsReported(true);
      window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
      // Récap championnat (manche/final) : masquer le overlay course locale pour
      // éviter deux « Continuer » empilés qui interceptent les clics.
      if (response.qualMancheRecap || response.qualFinalRecap) {
        this.raceResultsDismissed = true;
        this.raceHud?.showRaceResults(false);
        showChampionshipQualCompletionRecaps(response);
      }
      this.refresh();
    });
  }

  /** @deprecated Modale paramètres retirée — la caméra est dans la barre de dé. */
  public getViewSwitchElement(): HTMLElement | null {
    return null;
  }

  /** Résout l'identifiant du joueur "moi" pour le mode caméra `'me'` — en
   * multijoueur, `getMyPlayerIndex()` désigne CE client précis ; en solo/hotseat
   * local, on suit le premier emplacement humain faute de propriétaire unique. */
  private resolveMyCameraPlayerId(): number | null {
    if (!this.gameScene) return null;
    const mine = this.gameScene.getMyPlayerIndex();
    if (mine >= 0) return mine;
    const humans = this.gameScene.getHumanPlayerIndices();
    return humans.length > 0 ? humans[0] : null;
  }

  private setCameraMode(mode: CameraFollowMode): void {
    this.cameraMode = mode;
    this.circuit3dPreview?.setCameraMode?.(mode);
    this.raceHud?.setCameraModeActive(mode);
    this.syncCameraPickerUi();
    this.closeCameraPicker();
  }

  private syncCameraPickerUi(): void {
    if (this.cameraPickerLabel) {
      this.cameraPickerLabel.textContent = cameraModeLabel(this.cameraMode);
    }
    this.cameraPickerMenu
      ?.querySelectorAll<HTMLButtonElement>('[data-camera-mode]')
      .forEach((btn) => {
        const active = btn.dataset.cameraMode === this.cameraMode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
  }

  private openCameraPicker(): void {
    if (!this.cameraPickerMenu || !this.cameraPickerBtn || !this.cameraPickerRoot) return;
    this.cameraPickerMenu.hidden = false;
    this.cameraPickerBtn.setAttribute('aria-expanded', 'true');
    this.cameraPickerRoot.classList.add('is-open');
  }

  private closeCameraPicker(): void {
    if (!this.cameraPickerMenu || !this.cameraPickerBtn || !this.cameraPickerRoot) return;
    this.cameraPickerMenu.hidden = true;
    this.cameraPickerBtn.setAttribute('aria-expanded', 'false');
    this.cameraPickerRoot.classList.remove('is-open');
  }

  private toggleCameraPicker(): void {
    if (this.cameraPickerMenu?.hidden === false) this.closeCameraPicker();
    else this.openCameraPicker();
  }

  private buildCameraPicker(): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'play-dice-bar-camera';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'play-dice-bar-camera-btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Mode caméra');
    btn.title = 'Mode caméra';

    const label = document.createElement('span');
    label.className = 'play-dice-bar-camera-label';
    label.textContent = cameraModeLabel(this.cameraMode);
    const chevron = document.createElement('span');
    chevron.className = 'play-dice-bar-camera-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '▾';
    btn.append(label, chevron);

    const menu = document.createElement('ul');
    menu.className = 'play-dice-bar-camera-menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Mode caméra');
    menu.hidden = true;

    for (const { mode, label: modeLabel } of CAMERA_MODE_OPTIONS) {
      const li = document.createElement('li');
      li.setAttribute('role', 'presentation');
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'play-dice-bar-camera-option';
      opt.dataset.cameraMode = mode;
      opt.setAttribute('role', 'option');
      opt.setAttribute('aria-selected', mode === this.cameraMode ? 'true' : 'false');
      opt.classList.toggle('is-active', mode === this.cameraMode);
      opt.textContent = modeLabel;
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setCameraMode(mode);
      });
      li.appendChild(opt);
      menu.appendChild(li);
    }

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleCameraPicker();
    });

    root.append(btn, menu);
    this.cameraPickerRoot = root;
    this.cameraPickerBtn = btn;
    this.cameraPickerLabel = label;
    this.cameraPickerMenu = menu;

    this.cameraPickerDocClose = (e: MouseEvent) => {
      if (!(e.target instanceof Node) || !root.contains(e.target)) {
        this.closeCameraPicker();
      }
    };
    document.addEventListener('click', this.cameraPickerDocClose);

    return root;
  }

  /** Lance une course solo de test depuis #course?circuit=4&testRace=1 */
  public async tryBootstrapTestRaceFromHash(): Promise<void> {
    if (this.testRaceBootstrapDone) return;
    const { circuitId, testRace } = parseCourseHashParams();
    if (!testRace || circuitId == null || !this.gameScene) return;
    this.testRaceBootstrapDone = true;
    const divisionId = 4 as 1 | 2 | 3 | 4;
    try {
      const [circuit, carsList, driversList] = await Promise.all([
        fetchCircuitById(circuitId),
        fetchCarsByDivision(divisionId),
        fetchDriversByDivision(divisionId),
      ]);
      this.cars = carsList;
      this.drivers = driversList;
      this.gameScene.loadCircuit(circuit.circuit, circuit.name);
      sessionStorage.setItem('multirx-selected-circuit-id', String(circuitId));
      if (this.selectCircuit) this.selectCircuit.value = String(circuitId);
      if (this.selectDivision) this.selectDivision.value = String(divisionId);
      if (this.selectLaps) this.selectLaps.value = '1';
      if (this.selectParticipants) this.selectParticipants.value = '2';
      this.updateHumansOptions();
      if (this.selectHumans) this.selectHumans.value = '1';
      this.fillCarDriverSelects();
      this.playMode = 'solo';
      this.syncModeChoiceUi();
      this.gameScene.startRace(1, 2, 1, driversList, carsList, divisionId);
      this.goToSetupStep(Math.max(0, STEPS_BY_MODE[this.playMode].length - 1));
      this.refresh();
    } catch (e) {
      this.testRaceBootstrapDone = false;
      if (this.labelRaceFinished) {
        this.labelRaceFinished.style.display = 'block';
        this.labelRaceFinished.textContent =
          e instanceof Error ? e.message : 'Impossible de lancer la course de test.';
      }
    }
  }

  /** Lance une manche championnat depuis le hash #course?meetingSession=&driverId= */
  public async tryBootstrapMeetingFromHash(): Promise<void> {
    if (this.meetingBootstrapDone) return;
    const { meetingSession, driverId } = parseCourseHashParams();
    if (meetingSession == null || driverId == null || !this.gameScene) return;
    this.meetingBootstrapDone = true;
    this.championshipMode = true;
    // Relecture de la préférence Auto (le checkbox survit entre manches Phaser).
    syncAutoRollCheckbox(this.checkboxAutoRoll);
    try {
      const config = await fetchMeetingPlayConfig(meetingSession, driverId);
      this.meetingPlayConfig = config;
      const [circuit, carsList, driversList] = await Promise.all([
        fetchCircuitById(config.circuitId),
        fetchCarsByDivision(config.division),
        fetchDriversByDivision(config.division),
      ]);
      this.cars = carsList;
      this.drivers = driversList;
      this.gameScene.loadCircuit(circuit.circuit, circuit.name);
      if (this.selectLaps) this.selectLaps.value = String(config.numLaps);
      this.gameScene.startMeetingRace(config, driversList, carsList);
      // Reload / entrée directe #course : sans reclaim, aucun dice:state → softlock.
      emitMeetingReclaimRace(meetingSession, driverId);
      this.fillCarDriverSelects();
      this.goToSetupStep(Math.max(0, STEPS_BY_MODE[this.playMode].length - 1));
      this.refresh();
    } catch (e) {
      this.championshipMode = false;
      this.meetingPlayConfig = null;
      this.meetingBootstrapDone = false;
      if (this.labelRaceFinished) {
        this.labelRaceFinished.style.display = 'block';
        this.labelRaceFinished.textContent =
          e instanceof Error ? e.message : 'Impossible de charger la manche championnat.';
      }
    }
  }

  private createUI(container: HTMLElement): void {
    const wrapper = document.createElement('div');
    wrapper.id = 'dice-ui-wrapper';
    wrapper.className = 'dice-ui-wrapper';
    if (container.id === 'course-dice-mount' || container.closest?.('.course-tools-panel')) {
      wrapper.classList.add('dice-ui-wrapper-in-sidebar');
    }

    const panel = document.createElement('div');
    panel.className = 'dice-ui-panel';
    panel.setAttribute('aria-label', 'Dé et tour de jeu');

    this.blockPreRace = document.createElement('div');
    this.blockPreRace.className = 'dice-ui-block dice-ui-block--setup';
    this.buildSetupWizard(this.blockPreRace);
    panel.appendChild(this.blockPreRace);

    this.labelRaceFinished = document.createElement('div');
    this.labelRaceFinished.className = 'dice-ui-race-finished';
    this.inRacePlayingBlock = document.createElement('div');
    this.inRacePlayingBlock.className = 'play-dice-bar';
    this.inRacePlayingBlock.setAttribute('role', 'region');
    this.inRacePlayingBlock.setAttribute('aria-label', 'Lancer de dé');

    const turnCell = document.createElement('div');
    turnCell.className = 'play-dice-bar-turn';
    const turnPrefix = document.createElement('span');
    turnPrefix.className = 'play-dice-bar-turn-prefix';
    turnPrefix.textContent = 'Au tour de';
    this.labelTurn = document.createElement('span');
    this.labelTurn.className = 'play-dice-bar-turn-name';
    this.labelTurn.textContent = '—';
    turnCell.append(turnPrefix, this.labelTurn);

    const midCell = document.createElement('div');
    midCell.className = 'play-dice-bar-mid';
    this.diceCube = createDiceCubeScene();
    const resultCell = document.createElement('div');
    resultCell.className = 'play-dice-bar-result';
    const resultLabel = document.createElement('span');
    resultLabel.className = 'play-dice-bar-result-label';
    resultLabel.textContent = 'Cases';
    this.labelResult = document.createElement('span');
    this.labelResult.className = 'play-dice-bar-result-value';
    this.labelResult.textContent = '—';
    this.labelBonus = document.createElement('span');
    this.labelBonus.className = 'play-dice-bar-bonus';
    this.labelBonus.setAttribute('aria-hidden', 'true');
    resultCell.append(resultLabel, this.labelResult, this.labelBonus);
    midCell.append(this.diceCube, resultCell);

    this.buttonRoll = document.createElement('button');
    this.buttonRoll.type = 'button';
    this.buttonRoll.className = 'play-dice-bar-btn dice-ui-btn dice-ui-btn--primary';
    this.buttonRoll.textContent = 'Lancer le dé';
    this.buttonRoll.addEventListener('click', () => this.handleRoll());

    const autoRollLabel = document.createElement('label');
    autoRollLabel.className = 'play-dice-bar-auto';
    autoRollLabel.title = 'Lancer automatique / manuel';
    this.checkboxAutoRoll = document.createElement('input');
    this.checkboxAutoRoll.type = 'checkbox';
    this.checkboxAutoRoll.className = 'play-dice-bar-auto-input';
    this.checkboxAutoRoll.setAttribute('aria-label', 'Lancer automatique');
    syncAutoRollCheckbox(this.checkboxAutoRoll);
    this.checkboxAutoRoll.addEventListener('change', () => {
      writeAutoRollPreference(this.checkboxAutoRoll!.checked);
      this.refresh();
    });
    const autoSwitch = document.createElement('span');
    autoSwitch.className = 'play-dice-bar-auto-switch';
    autoSwitch.setAttribute('aria-hidden', 'true');
    autoSwitch.innerHTML = `
      <svg viewBox="0 0 212.4992 84.4688" overflow="visible" focusable="false">
        <path pathLength="360" fill="none" stroke="currentColor"
          d="M 42.2496 0 A 42.24 42.24 90 0 0 0 42.2496 A 42.24 42.24 90 0 0 42.2496 84.4688 A 42.24 42.24 90 0 0 84.4992 42.2496 A 42.24 42.24 90 0 0 42.2496 0 A 42.24 42.24 90 0 0 0 42.2496 A 42.24 42.24 90 0 0 42.2496 84.4688 L 170.2496 84.4688 A 42.24 42.24 90 0 0 212.4992 42.2496 A 42.24 42.24 90 0 0 170.2496 0 A 42.24 42.24 90 0 0 128 42.2496 A 42.24 42.24 90 0 0 170.2496 84.4688 A 42.24 42.24 90 0 0 212.4992 42.2496 A 42.24 42.24 90 0 0 170.2496 0 L 42.2496 0" />
      </svg>
    `;
    const autoCaption = document.createElement('span');
    autoCaption.className = 'play-dice-bar-auto-caption';
    autoCaption.setAttribute('aria-hidden', 'true');
    autoRollLabel.append(this.checkboxAutoRoll, autoSwitch, autoCaption);

    this.hintNoDriver = document.createElement('p');
    this.hintNoDriver.className = 'play-dice-bar-hint dice-ui-hint';
    this.hintNoDriver.textContent = 'Choisissez un pilote pour lancer le dé.';

    this.inRacePlayingBlock.append(
      this.buildCameraPicker(),
      turnCell,
      midCell,
      this.buttonRoll,
      autoRollLabel,
      this.hintNoDriver
    );

    const gameMain = document.querySelector('#view-course .game-main');
    if (gameMain) {
      this.raceHud = new RaceHud(gameMain as HTMLElement);
      this.raceHud.mountFinishedBanner(this.labelRaceFinished);
      this.raceHud.mountDiceDock(this.inRacePlayingBlock);
      this.view3dContainer = document.createElement('div');
      this.view3dContainer.className = 'play-race-3d-background';
      this.view3dContainer.hidden = true;
      gameMain.appendChild(this.view3dContainer);
      this.raceHud.viewSwitchToggle.addEventListener('click', () => {
        this.setViewMode(this.viewMode === '2d' ? '3d' : '2d');
      });
      this.raceHud.cameraModeSwitch.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-camera-mode]');
        const mode = btn?.dataset.cameraMode as CameraFollowMode | undefined;
        if (mode) this.setCameraMode(mode);
      });
    }

    this.loadCarsAndDriversForDivision();
    wrapper.appendChild(panel);

    container.appendChild(wrapper);
    this.wrapper = wrapper;
    this.panel = panel;

    this.goToSetupStep(0);
    this.refresh();
  }

  private getActiveStepIds(): SetupStepId[] {
    return STEPS_BY_MODE[this.playMode];
  }

  private getActiveStepDef(index: number): SetupStepDef {
    const id = this.getActiveStepIds()[index];
    return SETUP_STEP_DEFS[id];
  }

  private getCurrentStepId(): SetupStepId {
    return this.getActiveStepIds()[this.currentSetupStep] ?? 'mode';
  }

  private buildSetupWizard(host: HTMLElement): void {
    const wizard = document.createElement('div');
    wizard.className = 'dice-setup-wizard';

    const stepper = document.createElement('div');
    stepper.className = 'dice-setup-progress';
    stepper.setAttribute('aria-label', 'Étapes de préparation');
    wizard.appendChild(stepper);
    this.rebuildSetupStepper(stepper);

    const panelsWrap = document.createElement('div');
    panelsWrap.className = 'dice-setup-panels';

    const panelMode = this.createSetupPanel(SETUP_STEP_DEFS.mode);
    const modeGroup = document.createElement('div');
    modeGroup.className = 'dice-setup-mode-options';
    modeGroup.setAttribute('role', 'radiogroup');
    modeGroup.setAttribute('aria-label', 'Type de partie');
    const modeChoices: { mode: PlayMode; title: string; desc: string }[] = [
      {
        mode: 'solo',
        title: 'Course solo',
        desc: 'Votre circuit, votre bolide : affrontez des pilotes contrôlés par l’ordinateur.',
      },
      {
        mode: 'multi-host',
        title: 'Créer une partie',
        desc: 'Rassemblez vos amis, préparez la course et ouvrez la salle — à vous de jouer !',
      },
      {
        mode: 'multi-join',
        title: 'Rejoindre une partie',
        desc: 'Une course est déjà lancée : choisissez votre pilote et votre voiture, c’est parti.',
      },
    ];
    modeChoices.forEach(({ mode, title, desc }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dice-setup-mode-option';
      btn.dataset.playMode = mode;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', mode === this.playMode ? 'true' : 'false');
      btn.innerHTML = `<span class="dice-setup-mode-option-title">${title}</span><span class="dice-setup-mode-option-desc">${desc}</span>`;
      btn.classList.toggle('is-selected', mode === this.playMode);
      btn.addEventListener('click', () => this.selectPlayMode(mode));
      modeGroup.appendChild(btn);
      this.modeChoiceEls.push(btn);
    });
    panelMode.appendChild(modeGroup);
    this.stepPanelsById.mode = panelMode;

    const panelCircuit = this.createSetupPanel(SETUP_STEP_DEFS.circuit);
    const circuitHint = document.createElement('p');
    circuitHint.className = 'dice-ui-hint';
    circuitHint.innerHTML =
      'Créer ou gérer vos tracés : <a href="#garage/circuits">page Circuits</a>';
    circuitHint.querySelector('a')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.hash = 'circuits';
    });
    panelCircuit.appendChild(circuitHint);
    const fieldCircuit = document.createElement('div');
    fieldCircuit.className = 'dice-setup-field';
    fieldCircuit.innerHTML = '<label for="dice-select-circuit">Circuit</label>';
    this.selectCircuit = document.createElement('select');
    this.selectCircuit.id = 'dice-select-circuit';
    const optCircuitPlaceholder = document.createElement('option');
    optCircuitPlaceholder.value = '';
    optCircuitPlaceholder.textContent = 'Chargement…';
    this.selectCircuit.appendChild(optCircuitPlaceholder);
    this.selectCircuit.addEventListener('change', () => {
      void this.handleCircuitChange().finally(() => this.updateSetupNavState());
    });
    appendFancySelect(fieldCircuit, this.selectCircuit);
    panelCircuit.appendChild(fieldCircuit);
    const fieldLaps = document.createElement('div');
    fieldLaps.className = 'dice-setup-field';
    fieldLaps.innerHTML = '<label for="dice-select-laps">Nombre de tours</label>';
    this.selectLaps = document.createElement('select');
    this.selectLaps.id = 'dice-select-laps';
    const selectLaps = this.selectLaps;
    [1, 2, 3, 4, 5, 6, 10].forEach((n) => {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      if (n === 3) opt.selected = true;
      selectLaps.appendChild(opt);
    });
    appendFancySelect(fieldLaps, this.selectLaps);
    panelCircuit.appendChild(fieldLaps);
    this.stepPanelsById.circuit = panelCircuit;

    const panelDivision = this.createSetupPanel(SETUP_STEP_DEFS.division);
    const fieldDivision = document.createElement('div');
    fieldDivision.className = 'dice-setup-field';
    fieldDivision.innerHTML = '<label for="dice-select-division">Division</label>';
    this.selectDivision = document.createElement('select');
    this.selectDivision.id = 'dice-select-division';
    DIVISIONS.forEach((d) => {
      const opt = document.createElement('option');
      opt.value = String(d.id);
      opt.textContent = `${d.shortLabel} — ${d.name}`;
      this.selectDivision!.appendChild(opt);
    });
    this.selectDivision.addEventListener('change', () => {
      this.loadCarsAndDriversForDivision();
      this.updateSetupNavState();
    });
    appendFancySelect(fieldDivision, this.selectDivision);
    panelDivision.appendChild(fieldDivision);
    this.stepPanelsById.division = panelDivision;

    const panelRoster = this.createSetupPanel(SETUP_STEP_DEFS.roster);
    const fieldParticipants = document.createElement('div');
    fieldParticipants.className = 'dice-setup-field';
    const labelParticipants = document.createElement('label');
    labelParticipants.htmlFor = 'dice-select-participants';
    labelParticipants.textContent = 'Nombre de participants';
    this.rosterParticipantsLabel = labelParticipants;
    fieldParticipants.appendChild(labelParticipants);
    this.selectParticipants = document.createElement('select');
    this.selectParticipants.id = 'dice-select-participants';
    Array.from({ length: 19 }, (_, i) => i + 2).forEach((n) => {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      this.selectParticipants!.appendChild(opt);
    });
    this.selectParticipants.addEventListener('change', () => {
      this.updateHumansOptions();
      this.updateSetupNavState();
    });
    appendFancySelect(fieldParticipants, this.selectParticipants);
    panelRoster.appendChild(fieldParticipants);
    const fieldHumans = document.createElement('div');
    fieldHumans.className = 'dice-setup-field';
    const labelHumans = document.createElement('label');
    labelHumans.htmlFor = 'dice-select-humans';
    labelHumans.textContent = 'Joueurs humains';
    this.rosterHumansLabel = labelHumans;
    fieldHumans.appendChild(labelHumans);
    this.selectHumans = document.createElement('select');
    this.selectHumans.id = 'dice-select-humans';
    this.selectHumans.addEventListener('change', () => this.updateSetupNavState());
    appendFancySelect(fieldHumans, this.selectHumans);
    panelRoster.appendChild(fieldHumans);
    this.updateHumansOptions();
    this.stepPanelsById.roster = panelRoster;

    const panelPlayer = this.createSetupPanel(SETUP_STEP_DEFS.player);
    const fieldCar = document.createElement('div');
    fieldCar.className = 'dice-setup-field';
    fieldCar.innerHTML = '<label for="dice-select-car">Voiture</label>';
    this.selectCar = document.createElement('select');
    this.selectCar.id = 'dice-select-car';
    this.selectCar.appendChild(document.createElement('option')).textContent = 'Chargement…';
    this.selectCar.addEventListener('change', () => {
      this.handleCarDriverChange();
      this.updateSetupNavState();
    });
    appendFancySelect(fieldCar, this.selectCar);
    panelPlayer.appendChild(fieldCar);
    const fieldDriver = document.createElement('div');
    fieldDriver.className = 'dice-setup-field';
    fieldDriver.innerHTML = '<label for="dice-select-driver">Pilote</label>';
    this.selectDriver = document.createElement('select');
    this.selectDriver.id = 'dice-select-driver';
    this.selectDriver.appendChild(document.createElement('option')).textContent = 'Chargement…';
    this.selectDriver.addEventListener('change', () => {
      this.handleCarDriverChange();
      this.updateSetupNavState();
    });
    appendFancySelect(fieldDriver, this.selectDriver);
    panelPlayer.appendChild(fieldDriver);
    this.stepPanelsById.player = panelPlayer;

    for (const id of ['mode', 'circuit', 'division', 'roster', 'player'] as SetupStepId[]) {
      const panel = this.stepPanelsById[id];
      if (panel) panelsWrap.appendChild(panel);
    }

    const nav = document.createElement('div');
    nav.className = 'dice-setup-nav';
    this.btnSetupBack = document.createElement('button');
    this.btnSetupBack.type = 'button';
    this.btnSetupBack.className = 'btn btn-ghost dice-setup-nav-back';
    this.btnSetupBack.textContent = 'Retour';
    this.btnSetupBack.addEventListener('click', () => this.handleSetupBack());
    this.btnSetupPrimary = document.createElement('button');
    this.btnSetupPrimary.type = 'button';
    this.btnSetupPrimary.className = 'btn btn-primary dice-setup-nav-primary';
    this.btnSetupPrimary.textContent = 'Continuer';
    this.btnSetupPrimary.addEventListener('click', () => this.handleSetupPrimary());
    nav.append(this.btnSetupBack, this.btnSetupPrimary);

    wizard.append(panelsWrap, nav);
    host.appendChild(wizard);
    this.setupStepperHost = stepper;
  }

  private rebuildSetupStepper(stepper?: HTMLElement): void {
    const host = stepper ?? this.setupStepperHost;
    if (!host) return;
    host.innerHTML = '';

    const track = document.createElement('div');
    track.className = 'dice-setup-progress-track';

    const bar = document.createElement('div');
    bar.className = 'dice-setup-progress-bar';
    track.appendChild(bar);

    const checksWrap = document.createElement('div');
    checksWrap.className = 'dice-setup-progress-checks';
    this.getActiveStepIds().forEach((stepId, index) => {
      const check = document.createElement('div');
      check.className = 'dice-setup-progress-check is-pending';
      check.dataset.setupStep = String(index);
      check.title = SETUP_STEP_DEFS[stepId].label;
      check.innerHTML = CHECKMARK_SVG;
      checksWrap.appendChild(check);
    });
    track.appendChild(checksWrap);

    const label = document.createElement('p');
    label.className = 'dice-setup-progress-label';

    host.append(track, label);
    this.setupProgressBar = bar;
    this.setupProgressChecks = [
      ...checksWrap.querySelectorAll<HTMLElement>('.dice-setup-progress-check'),
    ];
    this.setupProgressLabel = label;
    this.updateSetupProgressVisual();
  }

  private updateSetupProgressVisual(): void {
    const steps = this.getActiveStepIds();
    const total = steps.length;
    const current = this.currentSetupStep;
    const stepId = steps[current];
    const def = stepId ? SETUP_STEP_DEFS[stepId] : null;

    if (this.setupProgressBar) {
      const pct = total <= 1 ? 100 : (current / (total - 1)) * 100;
      this.setupProgressBar.style.width = `${pct}%`;
    }

    this.setupProgressChecks.forEach((el, i) => {
      el.classList.toggle('is-done', i < current);
      el.classList.toggle('is-active', i === current);
      el.classList.toggle('is-pending', i > current);
    });

    if (this.setupProgressLabel && def) {
      this.setupProgressLabel.textContent = `${def.label} · ${current + 1}/${total}`;
    }
  }

  private selectPlayMode(mode: PlayMode): void {
    const modeChanged = mode !== this.playMode;
    this.playMode = mode;
    this.syncModeChoiceUi();
    if (modeChanged) {
      this.rebuildSetupStepper();
      const steps = this.getActiveStepIds();
      const prevId = steps[Math.min(this.currentSetupStep, steps.length - 1)];
      const nextIndex = prevId ? Math.max(0, steps.indexOf(prevId)) : 0;
      this.goToSetupStep(nextIndex);
      this.updateRosterFieldLabels();
    } else {
      this.updateSetupNavState();
    }
  }

  private syncModeChoiceUi(): void {
    this.modeChoiceEls.forEach((btn) => {
      const selected = btn.dataset.playMode === this.playMode;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
  }

  private createSetupPanel(step: SetupStepDef): HTMLDivElement {
    const panel = document.createElement('div');
    panel.className = 'dice-setup-panel';
    panel.hidden = true;
    const title = document.createElement('h3');
    title.className = 'dice-setup-panel-title';
    title.textContent = step.title;
    const lead = document.createElement('p');
    lead.className = 'dice-setup-panel-lead';
    lead.textContent = step.lead;
    panel.append(title, lead);
    return panel;
  }

  private updateHumansOptions(): void {
    if (!this.selectHumans || !this.selectParticipants) return;
    const n = parseInt(this.selectParticipants.value, 10) || 2;
    const prev = this.selectHumans.value;
    this.selectHumans.innerHTML = '';
    for (let i = 1; i <= n; i++) {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = String(i);
      this.selectHumans.appendChild(opt);
    }
    const prevNum = parseInt(prev, 10);
    if (prevNum >= 1 && prevNum <= n) {
      this.selectHumans.value = prev;
    }
  }

  private validateSetupStepId(stepId: SetupStepId): boolean {
    switch (stepId) {
      case 'mode':
        return Boolean(this.playMode);
      case 'circuit':
        return Boolean(this.selectCircuit?.value && this.gameScene?.hasCircuit());
      case 'division':
        return Boolean(this.selectDivision?.value);
      case 'roster': {
        const participants = parseInt(this.selectParticipants?.value ?? '0', 10);
        const humans = parseInt(this.selectHumans?.value ?? '0', 10);
        return participants >= 2 && humans >= 1 && humans <= participants;
      }
      case 'player':
        if (this.championshipMode && this.meetingPlayConfig) return true;
        return Boolean(this.selectCar?.value && this.selectDriver?.value);
      default:
        return false;
    }
  }

  private isLastSetupStep(): boolean {
    return this.currentSetupStep >= this.getActiveStepIds().length - 1;
  }

  private getPrimaryActionLabel(): string {
    if (!this.isLastSetupStep()) return 'Continuer';
    switch (this.playMode) {
      case 'multi-host':
        return 'Créer la salle multijoueur';
      case 'multi-join':
        return 'Rejoindre la partie';
      default:
        return 'Lancer la course solo';
    }
  }

  private canLaunchFromWizard(): boolean {
    if (!this.validateSetupStepId('player')) return false;
    if (this.playMode === 'multi-join') return this.gameScene?.getSelectedDriver() != null;
    return Boolean(this.gameScene?.hasCircuit());
  }

  private goToSetupStep(index: number): void {
    const steps = this.getActiveStepIds();
    this.currentSetupStep = Math.max(0, Math.min(steps.length - 1, index));
    const activeId = steps[this.currentSetupStep];
    for (const id of Object.keys(this.stepPanelsById) as SetupStepId[]) {
      const panel = this.stepPanelsById[id];
      if (!panel) continue;
      const visible = id === activeId;
      panel.hidden = !visible;
      panel.classList.toggle('is-active', visible);
      if (id === activeId) this.applyStepPanelCopy(id);
    }
    this.updateSetupProgressVisual();
    if (this.btnSetupBack) this.btnSetupBack.disabled = this.currentSetupStep === 0;
    if (activeId === 'circuit') {
      void this.reloadCircuits().then(() => {
        if (this.selectCircuit?.value) {
          void this.handleCircuitChange();
        }
      });
    } else if (activeId === 'player') this.loadCarsAndDriversForPlayerStep();
    if (activeId === 'roster') this.updateRosterFieldLabels();
    this.updateSetupNavState();
  }

  private applyStepPanelCopy(stepId: SetupStepId): void {
    const panel = this.stepPanelsById[stepId];
    if (!panel) return;
    const title = panel.querySelector('.dice-setup-panel-title');
    const lead = panel.querySelector('.dice-setup-panel-lead');
    const def = SETUP_STEP_DEFS[stepId];

    if (stepId === 'player') {
      if (this.playMode === 'multi-join') {
        if (title) title.textContent = 'Votre équipage';
        if (lead)
          lead.textContent =
            'Choisissez le pilote et la voiture avec lesquels vous rejoignez la course en cours.';
      } else if (this.playMode === 'multi-host') {
        if (title) title.textContent = def.title;
        if (lead)
          lead.textContent =
            'Votre pilote et votre voiture pour ouvrir la salle et accueillir les autres.';
      } else {
        if (title) title.textContent = def.title;
        if (lead) lead.textContent = def.lead;
      }
      return;
    }

    if (title) title.textContent = def.title;
    if (lead) lead.textContent = def.lead;
  }

  private updateRosterFieldLabels(): void {
    const isMultiHost = this.playMode === 'multi-host';
    const isSolo = this.playMode === 'solo';
    if (this.rosterParticipantsLabel) {
      this.rosterParticipantsLabel.textContent = isMultiHost
        ? 'Nombre de voitures sur la piste'
        : 'Nombre de participants';
    }
    if (this.rosterHumansLabel) {
      this.rosterHumansLabel.textContent = isMultiHost
        ? 'Places pour joueurs en ligne (vous inclus)'
        : isSolo
          ? 'Pilotes que vous contrôlez'
          : 'Joueurs humains';
    }
    const panel = this.stepPanelsById.roster;
    const lead = panel?.querySelector('.dice-setup-panel-lead');
    if (lead) {
      if (isMultiHost) {
        lead.textContent =
          'Taille de la grille et nombre de places pour de vrais pilotes. Les autres seront aux commandes de l’ordinateur.';
      } else if (isSolo) {
        lead.textContent =
          'Combien de voitures au départ ? Indiquez combien seront pilotées par vous : les autres seront contrôlées par l’ordinateur.';
      } else {
        lead.textContent = SETUP_STEP_DEFS.roster.lead;
      }
    }
  }

  private updateSetupNavState(): void {
    const stepId = this.getCurrentStepId();
    const onLast = this.isLastSetupStep();
    if (this.btnSetupPrimary) {
      this.btnSetupPrimary.textContent = this.getPrimaryActionLabel();
      this.btnSetupPrimary.className = onLast
        ? 'btn btn-primary dice-setup-nav-primary dice-ui-btn dice-ui-btn--success'
        : 'btn btn-primary dice-setup-nav-primary';
      if (onLast) {
        this.btnSetupPrimary.disabled = !this.canLaunchFromWizard();
      } else {
        this.btnSetupPrimary.disabled = !this.validateSetupStepId(stepId);
      }
    }
  }

  private handleSetupPrimary(): void {
    if (this.isLastSetupStep()) {
      if (!this.canLaunchFromWizard()) return;
      if (this.playMode === 'multi-host') {
        this.handleStartMultiplayer();
      } else if (this.playMode === 'multi-join') {
        this.gameScene?.joinMultiplayerGame();
      } else {
        this.handleStartRace();
      }
      return;
    }
    if (!this.validateSetupStepId(this.getCurrentStepId())) return;
    if (this.getCurrentStepId() === 'circuit') {
      void this.handleCircuitChange().then(() => this.goToSetupStep(this.currentSetupStep + 1));
      return;
    }
    this.goToSetupStep(this.currentSetupStep + 1);
  }

  private handleSetupBack(): void {
    if (this.currentSetupStep > 0) this.goToSetupStep(this.currentSetupStep - 1);
  }

  public resetSetupWizard(): void {
    this.championshipMode = false;
    this.meetingPlayConfig = null;
    this.meetingBootstrapDone = false;
    this.playMode = 'solo';
    this.syncModeChoiceUi();
    this.rebuildSetupStepper();
    this.goToSetupStep(0);
  }

  private setDiceCubeFace(n: number, animate: boolean): void {
    if (!this.diceCube) return;
    setDiceCubeFace(this.diceCube, n, animate);
  }

  private applyDiceCellColor(): void {
    if (!this.diceCube || !this.gameScene) return;
    const scene = this.diceCube.classList.contains('dice-cube-scene')
      ? this.diceCube
      : this.diceCube.closest('.dice-cube-scene');
    if (!scene) return;
    for (const cls of TRACK_CELL_COLOR_CLASS_NAMES) {
      scene.classList.remove(cls);
    }
    const ctx = this.gameScene.getLastRollCellContext();
    const cellClass = trackCellBonusVisualClass(
      resolveTrackCellBonusVisual(ctx?.bonusCellType, ctx?.mainPathIndex)
    );
    if (cellClass) scene.classList.add(cellClass);
  }

  private loadCarsAndDriversForPlayerStep(): void {
    if (this.playMode === 'multi-join') {
      void Promise.all([fetchCars(), fetchDrivers()])
        .then(([carsList, driversList]) => {
          this.cars = carsList;
          this.drivers = driversList;
          this.fillCarDriverSelects();
        })
        .catch(() => {
          this.cars = [];
          this.drivers = [];
          this.fillCarDriverSelects();
        });
      return;
    }
    this.loadCarsAndDriversForDivision();
  }

  private loadCarsAndDriversForDivision(): void {
    const divisionId = parseInt(this.selectDivision?.value ?? '1', 10) || 1;
    void Promise.all([fetchCarsByDivision(divisionId), fetchDriversByDivision(divisionId)])
      .then(([carsList, driversList]) => {
        this.cars = carsList;
        this.drivers = driversList;
        this.fillCarDriverSelects();
      })
      .catch(() => {
        this.cars = [];
        this.drivers = [];
        this.fillCarDriverSelects();
      });
  }

  private fillCarDriverSelects(): void {
    const cfg = this.meetingPlayConfig;
    if (this.championshipMode && cfg) {
      const humanSlot = cfg.grid.find((slot) => slot.driverId === cfg.humanDriverId);
      const lockedDriverId = cfg.humanDriverId;
      const lockedCarId = humanSlot?.carId;

      if (this.selectDriver) {
        this.selectDriver.innerHTML = '';
        const driver = this.drivers.find((d) => d.id === lockedDriverId);
        const opt = document.createElement('option');
        opt.value = String(lockedDriverId);
        const lockedName = driver
          ? `${driver.firstName} ${driver.lastName}`
          : (humanSlot?.driverName ?? `Pilote ${lockedDriverId}`);
        const accountDriverId = getCurrentUser()?.driverId ?? null;
        opt.textContent =
          accountDriverId != null && lockedDriverId === accountDriverId
            ? `${lockedName} (vous)`
            : lockedName;
        this.selectDriver.appendChild(opt);
        this.selectDriver.value = String(lockedDriverId);
        this.selectDriver.disabled = true;
      }
      if (this.selectCar) {
        this.selectCar.innerHTML = '';
        const car = this.cars.find((c) => c.id === lockedCarId);
        const opt = document.createElement('option');
        opt.value = lockedCarId != null ? String(lockedCarId) : '';
        opt.textContent = car?.name ?? humanSlot?.carName ?? '—';
        this.selectCar.appendChild(opt);
        if (lockedCarId != null) this.selectCar.value = String(lockedCarId);
        this.selectCar.disabled = true;
      }
      // Forcer le duo meeting sur la scène (handleCarDriverChange no-op en championnat).
      const lockedDriver = this.drivers.find((d) => d.id === lockedDriverId) ?? null;
      const lockedCar =
        lockedCarId != null ? (this.cars.find((c) => c.id === lockedCarId) ?? null) : null;
      if (lockedDriver) this.gameScene?.setSelectedDriver(lockedDriver);
      if (lockedCar) this.gameScene?.setSelectedCar(lockedCar);
      this.updateSetupNavState();
      return;
    }

    if (this.selectCar) this.selectCar.disabled = false;
    if (this.selectDriver) this.selectDriver.disabled = false;

    const authUser = getCurrentUser();
    const ownDriverId = authUser?.driverId ?? null;
    const ownCarId = authUser?.carId ?? null;
    const isStaff = authUser?.role != null && canPlayOtherDriver(authUser.role);
    const lockToOwn = !isStaff && ownDriverId != null;

    const takenDriverIds = new Set(this.gameScene?.getTakenDriverIds() ?? []);
    const takenCarIds = new Set(this.gameScene?.getTakenCarIds() ?? []);
    const currentDriverId = this.gameScene?.getSelectedDriver()?.id;
    const currentCarId = this.gameScene?.getSelectedCar()?.id;
    if (currentDriverId) takenDriverIds.delete(currentDriverId);
    if (currentCarId) takenCarIds.delete(currentCarId);

    if (this.selectCar) {
      this.selectCar.innerHTML = '';
      const optEmpty = document.createElement('option');
      optEmpty.value = '';
      optEmpty.textContent = '— Choisir une voiture —';
      this.selectCar.appendChild(optEmpty);
      let availableCars = this.cars.filter((c) => c.id == null || !takenCarIds.has(c.id));
      if (lockToOwn && ownCarId != null) {
        availableCars = availableCars.filter((c) => c.id === ownCarId);
        if (availableCars.length === 0) {
          const own = this.cars.find((c) => c.id === ownCarId);
          if (own) availableCars = [own];
        }
      }
      for (const c of availableCars) {
        const opt = document.createElement('option');
        opt.value = String(c.id ?? '');
        opt.textContent = c.name ?? `#${c.id}`;
        this.selectCar.appendChild(opt);
      }
      const preferCarId = lockToOwn ? ownCarId : currentCarId;
      const keepCar =
        preferCarId && availableCars.some((c) => c.id === preferCarId)
          ? String(preferCarId)
          : availableCars[0]?.id != null
            ? String(availableCars[0].id)
            : '';
      this.selectCar.value = keepCar;
      if (lockToOwn) this.selectCar.disabled = true;
    }
    if (this.selectDriver) {
      this.selectDriver.innerHTML = '';
      const optEmpty = document.createElement('option');
      optEmpty.value = '';
      optEmpty.textContent = '— Choisir un pilote —';
      this.selectDriver.appendChild(optEmpty);
      let availableDrivers = this.drivers.filter((d) => d.id == null || !takenDriverIds.has(d.id));
      if (lockToOwn && ownDriverId != null) {
        availableDrivers = availableDrivers.filter((d) => d.id === ownDriverId);
        if (availableDrivers.length === 0) {
          const own = this.drivers.find((d) => d.id === ownDriverId);
          if (own) availableDrivers = [own];
        }
      }
      for (const d of availableDrivers) {
        const opt = document.createElement('option');
        opt.value = String(d.id ?? '');
        const ownLabel = ownDriverId != null && d.id === ownDriverId ? ' (vous)' : '';
        opt.textContent = `${d.firstName} ${d.lastName}${ownLabel}`;
        this.selectDriver.appendChild(opt);
      }
      const preferDriverId =
        ownDriverId != null && availableDrivers.some((d) => d.id === ownDriverId)
          ? ownDriverId
          : currentDriverId;
      const keepDriver =
        preferDriverId && availableDrivers.some((d) => d.id === preferDriverId)
          ? String(preferDriverId)
          : availableDrivers[0]?.id != null
            ? String(availableDrivers[0].id)
            : '';
      this.selectDriver.value = keepDriver;
      if (lockToOwn) this.selectDriver.disabled = true;
    }
    this.handleCarDriverChange();
    this.updateSetupNavState();
  }

  private handleCarDriverChange(): void {
    if (!this.gameScene) return;
    if (this.championshipMode && this.meetingPlayConfig) return;
    let car: Car | null = null;
    let driver: Driver | null = null;
    const carIdStr = this.selectCar?.value;
    if (carIdStr) {
      const id = parseInt(carIdStr, 10);
      car = this.cars.find((c) => c.id === id) ?? null;
    }
    const driverIdStr = this.selectDriver?.value;
    if (driverIdStr) {
      const id = parseInt(driverIdStr, 10);
      driver = this.drivers.find((d) => d.id === id) ?? null;
    }
    this.gameScene.setSelectedCar(car);
    this.gameScene.setSelectedDriver(driver);
  }

  /** Recharge la liste des circuits depuis l’API (après édition sur la page Circuits). */
  public reloadCircuits(): Promise<void> {
    return this.loadSavedCircuitsForDice();
  }

  private loadSavedCircuitsForDice(): Promise<void> {
    const previousValue = this.selectCircuit?.value ?? '';

    return fetchCircuits()
      .then((list) => {
        this.savedCircuits = list;
        if (!this.selectCircuit) return;
        this.selectCircuit.innerHTML = '';
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent =
          list.length === 0 ? 'Aucun circuit sauvegardé' : '— Choisir un circuit —';
        this.selectCircuit.appendChild(empty);
        for (const saved of list) {
          const opt = document.createElement('option');
          opt.value = String(saved.id);
          opt.textContent = circuitSelectOptionLabel(saved);
          this.selectCircuit.appendChild(opt);
        }

        const storedId = sessionStorage.getItem('multirx-selected-circuit-id');
        const storedIdNum = storedId ? parseInt(storedId, 10) : NaN;
        const preferredId =
          previousValue && list.some((c) => c.id === parseInt(previousValue, 10))
            ? previousValue
            : storedId && list.some((c) => c.id === storedIdNum)
              ? storedId
              : '';

        if (preferredId) {
          this.selectCircuit.value = preferredId;
        } else if (storedId) {
          sessionStorage.removeItem('multirx-selected-circuit-id');
        }

        this.updateSetupNavState();
      })
      .catch(() => {
        if (this.selectCircuit) {
          this.selectCircuit.innerHTML = '';
          const opt = document.createElement('option');
          opt.value = '';
          opt.textContent = 'Erreur chargement (vérifier le serveur)';
          this.selectCircuit.appendChild(opt);
        }
      });
  }

  /** Charge le tracé sélectionné depuis l’API (données à jour). */
  private handleCircuitChange(): Promise<void> {
    const idStr = this.selectCircuit?.value;
    if (!idStr || !this.gameScene) return Promise.resolve();
    const id = parseInt(idStr, 10);
    if (Number.isNaN(id)) return Promise.resolve();

    return fetchCircuitById(id)
      .then((saved) => {
        const idx = this.savedCircuits.findIndex((s) => s.id === id);
        if (idx >= 0) this.savedCircuits[idx] = saved;
        else this.savedCircuits.push(saved);
        sessionStorage.setItem('multirx-selected-circuit-id', String(id));
        if (saved.circuit) this.gameScene!.loadCircuit(saved.circuit, saved.name);
        this.refreshPreRace3DPreviewIfActive();
        this.updateSetupNavState();
      })
      .catch(() => {
        const cached = this.savedCircuits.find((s) => s.id === id);
        if (cached?.circuit) this.gameScene!.loadCircuit(cached.circuit, cached.name);
        this.refreshPreRace3DPreviewIfActive();
      });
  }

  private handleStartRace(): void {
    void this.handleCircuitChange().then(() => {
      if (!this.gameScene?.hasCircuit()) return;
      const driver = this.gameScene.getSelectedDriver();
      if (!driver) return;
      const laps = parseInt(this.selectLaps?.value ?? '3', 10) || 3;
      const participants = parseInt(this.selectParticipants?.value ?? '2', 10) || 2;
      const numHumanPlayers = Math.max(
        1,
        Math.min(participants, parseInt(this.selectHumans?.value ?? '1', 10) || 1)
      );
      const divisionId = (parseInt(this.selectDivision?.value ?? '4', 10) || 4) as 1 | 2 | 3 | 4;
      this.gameScene.startRace(
        laps,
        participants,
        numHumanPlayers,
        this.drivers,
        this.cars,
        divisionId
      );
    });
  }

  private handleStartMultiplayer(): void {
    void this.handleCircuitChange().then(() => {
      if (!this.gameScene?.hasCircuit()) return;
      if (!this.gameScene.getSelectedDriver()) return;
      const laps = parseInt(this.selectLaps?.value ?? '3', 10) || 3;
      const participants = parseInt(this.selectParticipants?.value ?? '2', 10) || 2;
      const numHumanSlots = Math.max(
        1,
        Math.min(participants, parseInt(this.selectHumans?.value ?? '1', 10) || 1)
      );
      const divisionId = (parseInt(this.selectDivision?.value ?? '4', 10) || 4) as 1 | 2 | 3 | 4;
      this.gameScene.startMultiplayerGame(
        laps,
        participants,
        numHumanSlots,
        this.drivers,
        this.cars,
        divisionId
      );
    });
  }

  public getPlayMode(): PlayMode {
    return this.playMode;
  }

  private async saveRaceHistoryAsync(): Promise<void> {
    if (!this.gameScene || this.gameScene.getHistorySaved()) return;
    if (this.championshipMode) {
      // Manche meeting server-autoritaire : le serveur sauvegarde déjà le
      // replay et complète la session dès la fin de course côté serveur
      // (`finalizeMeetingRaceIfFinished`) — voir `onMeetingRaceComplete` dans
      // le constructeur, qui marque `historySaved`/affiche le récap poussé.
      // Rien à faire ici, on ne fait surtout pas de second POST client.
      return;
    }
    const replay = this.gameScene.buildRaceHistoryReplay();
    if (replay.finalRanking.length === 0) {
      this.gameScene.setHistorySaved(true);
      return;
    }
    const cfg = this.meetingPlayConfig;
    const circuitIdRaw =
      this.championshipMode && cfg ? String(cfg.circuitId) : this.selectCircuit?.value;
    const circuitId = circuitIdRaw && circuitIdRaw !== '' ? parseInt(circuitIdRaw, 10) : null;
    const divisionId =
      this.championshipMode && cfg
        ? cfg.division
        : ((parseInt(this.selectDivision?.value ?? '1', 10) || 1) as 1 | 2 | 3 | 4);
    const playModeMap: Record<PlayMode, RaceHistoryPlayMode> = {
      solo: 'solo',
      'multi-host': 'multi-host',
      'multi-join': 'multi-join',
    };
    try {
      // Les snapshots détaillés partent déjà en streaming vers /api/dev/race-debug.
      // Ne pas renvoyer le tampon entier ici : JSON.stringify de ~2 Mo plante l’onglet.
      await flushClientRaceDebugLogNow();
      const detail = await saveRaceHistory({
        circuitId: circuitId != null && !Number.isNaN(circuitId) ? circuitId : null,
        division: divisionId,
        numLaps: this.gameScene.getNumLaps(),
        numParticipants: this.gameScene.getNumParticipants(),
        playMode: this.championshipMode ? 'solo' : playModeMap[this.playMode],
        replay,
        meetingSessionId: cfg?.sessionId ?? null,
        seasonId: cfg?.seasonId ?? null,
        humanDriverId: cfg?.humanDriverId ?? null,
      });
      this.gameScene.setHistorySaved(true, detail.id);
    } catch {
      // Une seule tentative : marquer sauvé pour ne pas relancer à chaque refresh
      // (sinon échec réseau → boucle refresh → freeze navigateur).
      this.gameScene.setHistorySaved(true);
    }
  }

  private reportRaceResultAsync(): void {
    if (!this.gameScene || this.championshipMode || this.gameScene.isChampionshipSession()) return;
    // Les courses libres n'alimentent pas le classement — seules les saisons championnat comptent.
    this.gameScene.setResultsReported(true);
  }

  private buildRaceResultsActionsHtml(): string {
    if (!this.gameScene) return '';
    const links: string[] = [];
    const historyId = this.gameScene.getSavedHistoryId();
    if (historyId != null) {
      links.push(
        `<a href="#archives/${historyId}" class="btn btn-secondary btn-sm">Revoir la course</a>`
      );
    }
    if (this.championshipMode) {
      links.push(`<a href="#dashboard" class="btn btn-secondary btn-sm">Tableau de bord</a>`);
    }
    links.push(`<a href="#saisons" class="btn btn-ghost btn-sm">Classement</a>`);
    const continueBtn = `<button type="button" class="btn btn-secondary btn-sm play-race-results-continue-btn">Continuer</button>`;
    const newRaceBtn = `<button type="button" class="btn btn-primary btn-sm play-race-results-new-btn">Nouvelle course</button>`;
    return `${continueBtn} ${newRaceBtn}${links.map((l) => ` ${l}`).join('')}`;
  }

  private continueAfterRaceResults(): void {
    if (this.championshipMode) {
      this.raceResultsDismissed = true;
      this.raceHud?.showRaceResults(false);
      window.location.hash = 'saisons';
      return;
    }
    this.dismissRaceResultsOverlay();
  }

  private dismissRaceResultsOverlay(): void {
    this.raceResultsDismissed = true;
    this.raceHud?.showRaceResults(false);
    this.refresh();
  }

  private bindRaceResultsActions(): void {
    const continueBtn = this.raceHud?.resultsActions.querySelector<HTMLButtonElement>(
      '.play-race-results-continue-btn'
    );
    if (continueBtn) {
      continueBtn.onclick = () => this.continueAfterRaceResults();
    }
    const btn = this.raceHud?.resultsActions.querySelector<HTMLButtonElement>(
      '.play-race-results-new-btn'
    );
    if (btn) {
      btn.onclick = () => {
        this.raceResultsDismissed = false;
        this.gameScene?.resetToPreRaceIfFinished();
      };
    }
  }

  private handleRoll(): void {
    if (!this.gameScene?.canRollDice()) return;
    const roll = this.gameScene.rollDiceAndMove();
    if (roll > 0) {
      this.setDiceCubeFace(roll, true);
      setTimeout(() => this.refresh(), 950);
    } else {
      this.refresh();
    }
  }

  /**
   * Met à jour l'affichage (bloc pré-course vs en course, tour, dernier lancer, boutons)
   */
  public refreshParticipantSelects(): void {
    this.fillCarDriverSelects();
  }

  /**
   * Bascule le fond du plateau entre le canvas Phaser (2D) et la vue 3D (POC).
   * Fonctionne aussi bien avant le départ (aperçu statique du circuit chargé,
   * voitures de démo génériques) que pendant la course (voitures live
   * synchronisées via `setLive3DSyncHandler`) — la présence d'un `snapshot`
   * de course en cours détermine la branche prise.
   *
   * Appelé avec `'3d'` alors qu'on y est déjà (`viewMode` ne change pas) ne
   * doit PAS no-opper sans vérification : c'est exactement ce qui se passe
   * quand une course démarre alors que l'aperçu pré-course était déjà en 3D
   * — il faut alors remonter la scène avec les vraies voitures/positions, pas
   * rester bloqué sur l'aperçu statique (voir `live3DIsLiveRace`).
   */
  private setViewMode(mode: '2d' | '3d'): void {
    if (!this.gameScene) return;
    if (mode === '2d') {
      if (this.viewMode === '2d') return;
      this.viewMode = '2d';
      this.live3DIsLiveRace = false;
      this.raceHud?.setViewMode('2d');
      this.gameScene.setCarHudRefreshHandler(null);
      this.gameScene.setLive3DSyncHandler(null);
      this.circuit3dPreview?.dispose();
      this.circuit3dPreview = null;
      if (this.view3dContainer) this.view3dContainer.hidden = true;
      return;
    }
    const snapshot = this.gameScene.getLiveRace3DSnapshot();
    const isLiveRace = snapshot != null;
    if (this.viewMode === '3d' && this.live3DIsLiveRace === isLiveRace) return;
    const circuit = snapshot?.circuit ?? this.gameScene.getCurrentCircuit();
    if (!circuit || !this.view3dContainer) return;
    this.viewMode = '3d';
    this.live3DIsLiveRace = isLiveRace;
    this.raceHud?.setViewMode('3d');
    this.view3dContainer.hidden = false;
    this.circuit3dPreview?.dispose();
    this.circuit3dPreview = mountCircuit3dPreview(
      this.view3dContainer,
      circuit,
      snapshot?.players,
      {
        getTooltipHtml: (playerId) => this.gameScene!.buildPlayerTooltipHtml(playerId),
        getMyPlayerId: () => this.resolveMyCameraPlayerId(),
        getActivePlayerId: () => this.gameScene?.getCurrentPlayerIndex() ?? null,
        circuitName: this.gameScene.getCurrentCircuitName(),
        raceSky: true,
      }
    );
    this.circuit3dPreview.setCameraMode?.(this.cameraMode);
    const pushCarHudBadges = (): void => {
      if (!this.circuit3dPreview?.setCarHudBadge || !this.gameScene) return;
      const n = this.gameScene.getNumParticipants();
      for (let i = 0; i < n; i++) {
        this.circuit3dPreview.setCarHudBadge?.(i, this.gameScene.getCarHudBadgeHtml(i));
      }
    };
    this.gameScene.setCarHudRefreshHandler(isLiveRace ? pushCarHudBadges : null);
    this.gameScene.setLive3DSyncHandler(
      isLiveRace
        ? (playerIndex, trackPos, durationMs) => {
            this.circuit3dPreview?.movePlayerTo?.(playerIndex, trackPos, durationMs);
          }
        : null,
      isLiveRace
        ? (playerIndex) => {
            this.circuit3dPreview?.removePlayer?.(playerIndex);
          }
        : null
    );
    if (isLiveRace) pushCarHudBadges();
  }

  /** Ré-affiche l'aperçu 3D pré-course avec le nouveau circuit s'il est déjà à
   * l'écran — sinon `setViewMode('3d')` no-opperait (mode déjà '3d') et
   * laisserait l'ancien tracé affiché après un changement de circuit dans
   * l'assistant. Sans effet une fois la course démarrée (voir `setViewMode`,
   * qui gère alors le flux live séparément). */
  private refreshPreRace3DPreviewIfActive(): void {
    if (!this.gameScene || this.viewMode !== '3d' || this.gameScene.getRaceStarted()) return;
    const circuit = this.gameScene.getCurrentCircuit();
    if (!circuit || !this.view3dContainer) return;
    this.circuit3dPreview?.dispose();
    this.circuit3dPreview = mountCircuit3dPreview(this.view3dContainer, circuit, undefined, {
      getMyPlayerId: () => this.resolveMyCameraPlayerId(),
      getActivePlayerId: () => this.gameScene?.getCurrentPlayerIndex() ?? null,
      circuitName: this.gameScene.getCurrentCircuitName(),
      raceSky: true,
    });
    this.circuit3dPreview.setCameraMode?.(this.cameraMode);
  }

  public refresh(): void {
    if (!this.gameScene) return;
    const raceStarted = this.gameScene.getRaceStarted();
    if (this.blockPreRace) this.blockPreRace.style.display = raceStarted ? 'none' : 'flex';
    if (this.panel) this.panel.classList.toggle('dice-ui-panel--in-race', raceStarted);
    this.raceHud?.setVisible(raceStarted);
    if (!this.gameScene.hasCircuit()) {
      this.setViewMode('2d');
      this.hasAutoSwitchedToLive3D = false;
    } else if (!this.hasAutoSwitchedToLive3D) {
      // Vue 3D par défaut dès qu'un circuit est chargé — aussi bien pour
      // l'aperçu pré-course que pour la course elle-même une fois lancée.
      // L'utilisateur reste libre de repasser en 2D ensuite (switch dans les
      // paramètres du plateau) ; ce refresh continu ne doit pas l'y reforcer,
      // d'où le drapeau (remis à zéro seulement quand le circuit est retiré).
      this.hasAutoSwitchedToLive3D = true;
      this.setViewMode('3d');
    } else {
      // Ré-affirme le mode courant à chaque refresh : no-op pour '2d', et
      // pour '3d' laisse `setViewMode` détecter si la course vient de
      // démarrer/finir pendant qu'on y était déjà (voir `live3DIsLiveRace`)
      // pour remonter la scène avec les vraies voitures au bon moment.
      this.setViewMode(this.viewMode);
    }

    if (raceStarted) {
      const raceFinished = this.gameScene.getRaceFinished();
      if (!raceFinished) {
        this.raceResultsDismissed = false;
      }
      if (raceFinished && this.raceHud) {
        this.raceHud.updateRaceResults(this.gameScene);
        if (!this.raceResultsDismissed) {
          this.raceHud.showRaceResults(true);
          this.raceHud.setResultsActionsHtml(this.buildRaceResultsActionsHtml());
          this.bindRaceResultsActions();
        } else {
          this.raceHud.showRaceResults(false);
        }
        if (
          shouldAttemptClientRaceHistorySave({
            championshipMode: this.championshipMode,
            historySaved: this.gameScene.getHistorySaved(),
            historySaveInFlight: this.historySaveInFlight,
          })
        ) {
          this.historySaveInFlight = true;
          void this.saveRaceHistoryAsync()
            .catch(() => undefined)
            .finally(() => {
              this.historySaveInFlight = false;
              this.refresh();
            });
        }
        if (!this.gameScene.getResultsReported()) {
          void this.reportRaceResultAsync();
        }
      } else if (this.raceHud) {
        this.raceHud.showRaceResults(false);
        if (this.labelRaceFinished) {
          this.labelRaceFinished.style.display = 'none';
        }
      }
      if (!raceFinished && !this.gameScene.getIsMultiplayer()) {
        this.gameScene.syncSoloTurnOrderBeforeDisplay();
      }
      if (this.labelTurn && this.labelResult && this.buttonRoll) {
        const canRoll = this.gameScene.canRollDice();
        const hasDriver = this.gameScene.getSelectedDriver() != null;
        const isHumanTurn = this.gameScene.getCurrentPlayerIsHuman();
        const needsJokerLap = this.gameScene.needsJokerLapEntryChoice();
        // Storage = source de vérité (évite softlock si checkbox désync).
        const autoRollOn = isAutoRollEnabled(this.checkboxAutoRoll);
        // Mode Auto : choix joker aléatoire (pas un refus systématique) une fois
        // le pion posé — évite softlock sans forcer le circuit principal.
        if (
          autoRollOn &&
          isHumanTurn &&
          needsJokerLap &&
          !this.gameScene.getIsMovingToken() &&
          this.gameScene.getPendingJokerLapEntryChoice() == null
        ) {
          this.gameScene.applyAutoJokerLapEntryChoice();
        }
        const needsJokerLapAfterAuto = this.gameScene.needsJokerLapEntryChoice();
        this.buttonRoll.disabled = !canRoll || !isHumanTurn || needsJokerLapAfterAuto;
        if (!this.buttonRoll.disabled) {
          if (autoRollOn && !this.autoRollPending) {
            this.autoRollPending = true;
            setTimeout(() => this.handleRoll(), AUTO_ROLL_DELAY_MS);
          }
        } else {
          this.autoRollPending = false;
        }
        this.gameScene.syncJokerLapChoiceOverlay();
        if (this.inRacePlayingBlock)
          this.inRacePlayingBlock.style.display = raceFinished ? 'none' : 'flex';
        if (this.hintNoDriver) {
          const meetingOk = this.championshipMode && this.meetingPlayConfig != null;
          this.hintNoDriver.hidden =
            ((hasDriver || meetingOk) && !needsJokerLapAfterAuto) || raceFinished;
          this.hintNoDriver.textContent = needsJokerLapAfterAuto
            ? 'Tour joker : choisissez circuit principal ou branche.'
            : 'Choisissez un pilote pour lancer le dé.';
        }
        this.inRacePlayingBlock?.classList.toggle(
          'play-dice-bar--waiting',
          !isHumanTurn && !raceFinished
        );
        const current = this.gameScene.getCurrentPlayerIndex();
        const isMulti = this.gameScene.getIsMultiplayer();
        const myIdx = this.gameScene.getMyPlayerIndex();
        const currentInfo = this.gameScene.getPlayerDisplayInfo(current);
        const currentName = labelOwnDriver(
          currentInfo.driverName,
          this.gameScene.getPlayerDriverId(current)
        );
        this.labelTurn.textContent = !isHumanTurn
          ? `${currentName} (simulation…)`
          : isMulti && myIdx >= 0 && current === myIdx
            ? `${currentName} · c'est vous`
            : currentName;
        const lastRoll = this.gameScene.getLastDiceRoll();
        const moveBudget = this.gameScene.getLastMoveBudget();
        const displayTotal = moveBudget > 0 ? moveBudget : this.gameScene.getLastEffectiveMove();
        const bonus = displayTotal > 0 && lastRoll > 0 ? displayTotal - lastRoll : 0;
        if (this.labelBonus) {
          this.labelBonus.textContent = bonus > 0 ? `+${bonus}` : '';
          this.labelBonus.hidden = bonus <= 0;
        }
        this.labelResult.textContent = displayTotal > 0 ? String(displayTotal) : '—';
        this.setDiceCubeFace(lastRoll > 0 ? lastRoll : 1, false);
        this.applyDiceCellColor();
      }
      this.refreshRaceStats();
    } else {
      this.updateSetupNavState();
    }
  }

  private refreshRaceStats(): void {
    if (!this.gameScene || !this.raceHud) return;
    this.raceHud.updateRanking(this.gameScene);
  }

  /** Rafraîchit uniquement le panneau classement / historique (sans tout le dock dé). */
  public refreshRaceHud(): void {
    this.refreshRaceStats();
  }

  public destroy(): void {
    this.meetingRaceCompleteTeardown?.();
    this.meetingRaceCompleteTeardown = null;
    if (this.cameraPickerDocClose) {
      document.removeEventListener('click', this.cameraPickerDocClose);
      this.cameraPickerDocClose = null;
    }
    this.cameraPickerRoot = null;
    this.cameraPickerBtn = null;
    this.cameraPickerLabel = null;
    this.cameraPickerMenu = null;
    const wrapper = document.getElementById('dice-ui-wrapper');
    if (wrapper) wrapper.remove();
    this.wrapper = null;
    this.panel = null;
    this.raceHud?.destroy();
    this.raceHud = null;
    this.gameScene?.setCarHudRefreshHandler(null);
    this.gameScene?.setLive3DSyncHandler(null);
    this.circuit3dPreview?.dispose();
    this.circuit3dPreview = null;
    this.view3dContainer?.remove();
    this.view3dContainer = null;
    this.viewMode = '2d';
    this.hasAutoSwitchedToLive3D = false;
    this.blockPreRace = null;
    this.selectCircuit = null;
    this.savedCircuits = [];
    this.selectCar = null;
    this.selectDriver = null;
    this.selectParticipants = null;
    this.selectHumans = null;
    this.selectDivision = null;
    this.selectLaps = null;
    this.rosterParticipantsLabel = null;
    this.rosterHumansLabel = null;
    this.setupProgressBar = null;
    this.setupProgressChecks = [];
    this.setupProgressLabel = null;
    this.stepPanelsById = {};
    this.modeChoiceEls = [];
    this.setupStepperHost = null;
    this.btnSetupBack = null;
    this.btnSetupPrimary = null;
    this.buttonRoll = null;
    this.labelTurn = null;
    this.labelResult = null;
    this.labelBonus = null;
    this.diceCube = null;
    this.hintNoDriver = null;
    this.labelRaceFinished = null;
    this.inRacePlayingBlock = null;
    this.gameScene = null;
  }
}
